import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { ChevronRight, User as UserIcon } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Pagination, clean } from "@/components/Pagination"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Autocomplete } from "@/components/ui/autocomplete"
import { useUserSearch } from "@/hooks/use-user-search"
import type { Pagination as PaginationMeta } from "@/types/pagination"
import type { PageProps } from "@/types/inertia"

type UserRow = {
  id: number
  name: string
  email: string | null
  organization: string | null
  admin: boolean
  /** Created by the weekly-report import; has never signed in. */
  placeholder: boolean
  created_at: string
}

type Props = {
  users: UserRow[]
  filters: { q: string | null }
  /** Over every account, not over the page or the search. */
  counts: { total: number; placeholders: number }
  pagination: PaginationMeta
}

const DESCRIPTION = "Find an account, and manage admin access, in the admin area."

export default function AdminUsersIndex({ users, filters, counts, pagination }: Props) {
  const { props } = usePage<PageProps>()
  const currentUserId = props.current_user?.id

  const [query, setQuery] = React.useState(filters.q ?? "")
  const { suggestions, loading } = useUserSearch(query)

  // Typing narrows the list below. Debounced separately from the dropdown so
  // the two can disagree for a moment without either feeling stuck, and always
  // back to page one — staying on page seven of a list that just shrank to two
  // pages is never what somebody meant.
  React.useEffect(() => {
    if ((filters.q ?? "") === query) return

    const timer = window.setTimeout(() => {
      router.get("/admin/users", clean({ q: query }), {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    }, 300)

    return () => window.clearTimeout(timer)
  }, [query, filters.q])

  const signedIn = counts.total - counts.placeholders

  const setAdmin = (user: UserRow, admin: boolean) => {
    router.patch(
      `/admin/users/${user.id}/role`,
      { admin },
      { preserveScroll: true },
    )
  }

  return (
    <>
      <Head title="Users">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Users" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Users"
          description={`${signedIn} ${signedIn === 1 ? "person has" : "people have"} signed in. Accounts are created by Duo on first sign-in — there is no way to add one here.${
            counts.placeholders > 0
              ? ` A further ${counts.placeholders} were created by the weekly-report import and are claimed when those people first sign in.`
              : ""
          }`}
        />

        <div className="mt-6">
          <FlashNotice />

          <div className="mb-4 max-w-md">
            <label htmlFor="user-search">Search</label>
            <Autocomplete
              id="user-search"
              className="mt-1"
              value={query}
              onValueChange={setQuery}
              suggestions={suggestions.map((person) => ({
                id: person.id,
                label: person.name,
                hint: [person.email, person.organization].filter(Boolean).join(" · ") || null,
              }))}
              loading={loading}
              onSelect={(suggestion) => router.visit(`/admin/users/${suggestion.id}`)}
              placeholder="Name, email, username or organization"
              hintMessage="Type a name, an email, a username or an organization."
              emptyMessage="No accounts match."
              aria-label="Search accounts"
            />
          </div>

          {pagination.total_count === 0 ? (
            <div className="callout">
              <p className="m-0">
                No accounts match “{filters.q}”. Search on a name, an email address, a
                username, or the name of an organization.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
                {users.map((user) => {
                  const isSelf = user.id === currentUserId

                  return (
                    <li key={user.id} className="flex items-center gap-3 pr-4">
                      <Link
                        href={`/admin/users/${user.id}`}
                        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                      >
                        <UserIcon className="h-4 w-4 shrink-0 text-ink-muted" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 truncate text-sm font-medium text-ink-display">
                            <span className="truncate">{user.name}</span>
                            {user.placeholder && <Badge tone="muted">Never signed in</Badge>}
                          </div>
                          <div className="truncate text-xs text-ink-muted">
                            {user.email ?? "no email"}
                            {user.organization ? ` · ${user.organization}` : ""}
                            {user.placeholder
                              ? ` · imported ${formatDate(user.created_at)}`
                              : ` · first signed in ${formatDate(user.created_at)}`}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                      </Link>

                      <label
                        className="flex shrink-0 items-center gap-2 text-xs font-normal text-ink-body"
                        title={
                          isSelf
                            ? "You can't change your own admin access."
                            : `Admin access for ${user.name}`
                        }
                      >
                        <Checkbox
                          checked={user.admin}
                          disabled={isSelf}
                          onChange={(event) => setAdmin(user, event.target.checked)}
                          aria-label={`Admin access for ${user.name}`}
                        />
                        Admin
                      </label>
                    </li>
                  )
                })}
              </ul>

              <Pagination
                meta={pagination}
                path="/admin/users"
                params={{ q: filters.q }}
                label="accounts"
              />
            </>
          )}

          <p className="mt-3 text-xs text-ink-muted">
            Admins can define automations and promote other admins. You can't
            change your own access, so the last admin can't lock everyone out.
          </p>
        </div>
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
